import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { DashboardLayout } from "@/components/DashboardLayout";
import { KCard } from "@/components/KCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/auth/AuthContext";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  BookOpen,
  FileText,
  Plus,
  Radio,
  Trash2,
  Upload,
  Video,
  Volume2,
  ArrowLeft,
  UserPlus,
  GripVertical,
  ChevronDown,
  ChevronRight,
  ClipboardList,
  Globe,
  EyeOff,
  Layers,
  PenLine,
  X,
  Check,
  ArrowUp,
  ArrowDown,
} from "lucide-react";
import {
  createChapter,
  createLesson,
  deleteChapter,
  deleteLesson,
  getCourse,
  getCourseChapters,
  getCourseTeachers,
  assignCourseTeacher,
  updateCourse,
  type ChapterDetails,
  type Course,
  type LessonDetails,
  uploadLessonPdf,
  uploadLessonVideo,
  uploadLessonAudio,
  updateChapter,
  updateLesson,
  reorderChapters,
  reorderLessons,
} from "@/api/lms/courses";
import { getCourseExams, type Exam } from "@/api/lms/exams";
import { getAdminUsers } from "@/api/admin/users";
import { cn } from "@/lib/utils";

// --- Helpers ---

function lessonTypeIcon(type: LessonDetails["type"], size = 14) {
  if (type === "VIDEO") return <Video size={size} className="text-primary shrink-0" />;
  if (type === "PDF") return <FileText size={size} className="text-amber-500 shrink-0" />;
  if (type === "AUDIO") return <Volume2 size={size} className="text-emerald-500 shrink-0" />;
  return <Radio size={size} className="text-violet-500 shrink-0" />;
}

function lessonTypeBadge(type: LessonDetails["type"]) {
  const map: Record<string, string> = {
    VIDEO: "bg-primary/10 text-primary",
    PDF: "bg-amber-50 text-amber-600",
    AUDIO: "bg-emerald-50 text-emerald-600",
    LIVE: "bg-violet-50 text-violet-600",
  };
  return map[type] ?? "bg-secondary text-muted-foreground";
}

function parseDurationSeconds(raw: string): number | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const n = Number.parseInt(trimmed, 10);
  if (!Number.isFinite(n)) return null;
  return Math.max(0, n);
}

function toDatetimeLocalValue(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  const h = String(d.getHours()).padStart(2, "0");
  const min = String(d.getMinutes()).padStart(2, "0");
  return `${y}-${m}-${day}T${h}:${min}`;
}

function ThemedFilePicker({
  accept,
  disabled,
  onPick,
  label = "Choose file",
}: {
  accept: string;
  disabled?: boolean;
  onPick: (file: File) => void;
  label?: string;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.currentTarget.value = "";
          if (file) onPick(file);
        }}
      />
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={disabled}
        className="shrink-0 gap-1.5"
        onClick={() => inputRef.current?.click()}
      >
        <Upload className="h-3.5 w-3.5 shrink-0" />
        {label}
      </Button>
    </>
  );
}

// --- Teacher Assignment Section ---

function TeacherAssignmentSection({
  courseId,
  isAdmin,
}: {
  courseId: string;
  isAdmin: boolean;
}) {
  const queryClient = useQueryClient();
  const [teacherToAssignId, setTeacherToAssignId] = useState<string>("");
  const [open, setOpen] = useState(false);

  const courseTeachersQuery = useQuery({
    queryKey: ["courseTeachers", courseId],
    queryFn: () => getCourseTeachers(courseId),
    enabled: isAdmin && Boolean(courseId),
  });

  const teachersCandidatesQuery = useQuery({
    queryKey: ["adminTeachers", 1, 100],
    queryFn: async () => {
      const res = await getAdminUsers({ page: 1, limit: 100 });
      return res.data.filter((u) => u.role === "TEACHER");
    },
    enabled: isAdmin,
  });

  const assignTeacherMutation = useMutation({
    mutationFn: (teacherId: number) => assignCourseTeacher(courseId, teacherId),
    onSuccess: () => {
      toast.success("Teacher assigned to course");
      setTeacherToAssignId("");
      queryClient.invalidateQueries({ queryKey: ["courseTeachers", courseId] });
    },
    onError: (err: any) => {
      toast.error(err?.message || "Failed to assign teacher");
    },
  });

  const assignedTeacherIds = useMemo(() => {
    return new Set((courseTeachersQuery.data ?? []).map((t) => t.teacherId));
  }, [courseTeachersQuery.data]);

  const availableTeacherOptions = useMemo(() => {
    const candidates = teachersCandidatesQuery.data ?? [];
    return candidates.filter((u) => !assignedTeacherIds.has(u.id));
  }, [teachersCandidatesQuery.data, assignedTeacherIds]);

  if (!isAdmin) return null;

  const assignedTeachers = courseTeachersQuery.data ?? [];

  return (
    <div className="mb-6">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between gap-3 p-4 bg-card border border-border rounded-xl hover:bg-muted/30 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-violet-50 flex items-center justify-center shrink-0">
            <UserPlus size={15} className="text-violet-600" />
          </div>
          <div className="text-left">
            <p className="text-sm font-semibold text-foreground">Teacher Access</p>
            <p className="text-xs text-muted-foreground">
              {assignedTeachers.length === 0
                ? "No teachers assigned"
                : `${assignedTeachers.length} teacher${assignedTeachers.length > 1 ? "s" : ""} assigned`}
            </p>
          </div>
        </div>
        <ChevronDown
          size={15}
          className={cn("text-muted-foreground transition-transform duration-200 shrink-0", open && "rotate-180")}
        />
      </button>

      {open && (
        <div className="mt-2 p-4 bg-card border border-border rounded-xl space-y-4">
          {/* Assigned teachers */}
          {assignedTeachers.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {assignedTeachers.map((ct) => (
                <span
                  key={ct.id}
                  className="px-3 py-1 text-xs font-medium rounded-full bg-violet-50 text-violet-700 border border-violet-100"
                >
                  {ct.teacher?.email ?? `Teacher #${ct.teacherId}`}
                </span>
              ))}
            </div>
          )}

          {/* Assign new teacher */}
          <div className="flex flex-col sm:flex-row gap-3">
            <select
              className="flex-1 h-9 px-3 rounded-lg border border-border bg-background text-sm text-foreground"
              value={teacherToAssignId}
              onChange={(e) => setTeacherToAssignId(e.target.value)}
              disabled={teachersCandidatesQuery.isPending || assignTeacherMutation.isPending}
            >
              <option value="">Select a teacher to assign…</option>
              {availableTeacherOptions.map((t) => (
                <option key={t.id} value={String(t.id)}>
                  {t.email}
                </option>
              ))}
            </select>
            <Button
              onClick={() => {
                if (!teacherToAssignId) return;
                const id = Number.parseInt(teacherToAssignId, 10);
                if (!Number.isFinite(id)) return;
                assignTeacherMutation.mutate(id);
              }}
              disabled={!teacherToAssignId || assignTeacherMutation.isPending}
              size="sm"
              className="gap-1.5 shrink-0"
            >
              <UserPlus size={14} />
              {assignTeacherMutation.isPending ? "Assigning…" : "Assign"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// --- Lesson Row ---

function LessonRow({
  courseId,
  chapterId,
  lesson,
  index,
  onDelete,
  disabled,
  onMoveUp,
  onMoveDown,
  isLast,
}: {
  courseId: string;
  chapterId: string;
  lesson: LessonDetails;
  index: number;
  onDelete: (lesson: LessonDetails) => void;
  disabled?: boolean;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  isLast?: boolean;
}) {
  const queryClient = useQueryClient();
  const [expanded, setExpanded] = useState(false);
  const [draft, setDraft] = useState<LessonDetails | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);

  const effectiveLesson = draft ?? lesson;

  const updateLessonMutation = useMutation({
    mutationFn: (dto: any) => updateLesson(courseId, chapterId, lesson.id, dto),
    onSuccess: () => {
      toast.success("Lesson updated");
      setDraft(null);
      queryClient.invalidateQueries({ queryKey: ["courseChapters", courseId] });
      queryClient.invalidateQueries({ queryKey: ["adminCourse", courseId] });
    },
    onError: (err: any) => {
      toast.error(err?.message || "Failed to update lesson");
    },
  });

  const uploadFileMutation = useMutation({
    mutationFn: async (file: File) => {
      setUploadProgress(0);
      const type = effectiveLesson.type;
      const uploader =
        type === "VIDEO"
          ? uploadLessonVideo
          : type === "PDF"
            ? uploadLessonPdf
            : uploadLessonAudio;
      const uploaded = await uploader(file, setUploadProgress);
      return updateLesson(courseId, chapterId, lesson.id, {
        contentUrl: uploaded.url,
        // Store S3 key for secure signed URL generation
        ...(uploaded.key ? { contentKey: uploaded.key } : {}),
      });
    },
    onSuccess: () => {
      toast.success("File uploaded and saved");
      setDraft(null);
      setUploadProgress(0);
      queryClient.invalidateQueries({ queryKey: ["courseChapters", courseId] });
    },
    onError: (err: any) => {
      toast.error(err?.message || "Failed to upload or save file");
      setUploadProgress(0);
    },
  });

  const handleUpdate = () => {
    const dto: any = {
      title: effectiveLesson.title.trim(),
      type: effectiveLesson.type,
      order: index,
      isPreview: Boolean(effectiveLesson.isPreview),
      description: effectiveLesson.description !== undefined ? (effectiveLesson.description?.trim() || null) : undefined,
      objectives: effectiveLesson.objectives !== undefined ? (effectiveLesson.objectives?.trim() || null) : undefined,
    };
    if (typeof effectiveLesson.duration === "number") dto.duration = effectiveLesson.duration;
    const link = effectiveLesson.contentUrl?.trim();
    if (effectiveLesson.type === "VIDEO" || effectiveLesson.type === "PDF" || effectiveLesson.type === "AUDIO") {
      dto.contentUrl = link ? link : null;
      dto.liveStartAt = null;
      dto.liveEndAt = null;
    } else if (effectiveLesson.type === "LIVE") {
      dto.contentUrl = link ? link : null;
      dto.liveStartAt = effectiveLesson.liveStartAt ?? null;
      dto.liveEndAt = effectiveLesson.liveEndAt ?? null;
    }
    updateLessonMutation.mutate(dto);
  };

  const isSaving = updateLessonMutation.isPending || uploadFileMutation.isPending;
  const isBusy = isSaving || disabled;

  return (
    <div
      className={cn(
        "group rounded-lg border border-border/60 bg-card overflow-hidden transition-all duration-150",
        expanded && "shadow-sm",
      )}
    >
      {/* Row header */}
      <div className="flex items-center gap-2.5 px-3 py-2.5">
        {lessonTypeIcon(effectiveLesson.type)}
        <span className="flex-1 text-sm font-medium text-foreground truncate">{effectiveLesson.title}</span>
        {effectiveLesson.isPreview && (
          <span className="text-[10px] font-bold bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 px-2 py-0.5 rounded-full shrink-0">
            Preview
          </span>
        )}
        {effectiveLesson.duration && (
          <span className="text-[11px] text-muted-foreground shrink-0">{Math.round(effectiveLesson.duration / 60)}m</span>
        )}
        <span className={cn(
          "text-[10px] font-semibold px-2 py-0.5 rounded-full shrink-0",
          lessonTypeBadge(effectiveLesson.type),
        )}>
          {effectiveLesson.type}
        </span>
        {/* Up/Down reorder buttons */}
        <div className="flex items-center gap-0.5 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            type="button"
            className="text-muted-foreground hover:text-foreground disabled:opacity-30 p-1 rounded transition-colors"
            disabled={index === 0 || isBusy}
            onClick={(e) => {
              e.stopPropagation();
              onMoveUp?.();
            }}
            title="Move up"
          >
            <ArrowUp size={11} />
          </button>
          <button
            type="button"
            className="text-muted-foreground hover:text-foreground disabled:opacity-30 p-1 rounded transition-colors"
            disabled={isLast || isBusy}
            onClick={(e) => {
              e.stopPropagation();
              onMoveDown?.();
            }}
            title="Move down"
          >
            <ArrowDown size={11} />
          </button>
        </div>

        <button
          onClick={() => setExpanded(!expanded)}
          className="text-muted-foreground hover:text-foreground transition-colors p-1 rounded"
          title="Edit lesson"
        >
          <PenLine size={13} />
        </button>
        <button
          onClick={() => onDelete(lesson)}
          className="text-muted-foreground hover:text-destructive transition-colors p-1 rounded opacity-0 group-hover:opacity-100"
          disabled={isBusy}
        >
          <Trash2 size={13} />
        </button>
      </div>

      {/* Expanded edit form */}
      {expanded && (
        <div className="border-t border-border/60 p-4 bg-muted/20 space-y-4">
          <div className="flex flex-wrap items-start gap-3">
            {/* Title */}
            <div className="flex-1 min-w-[160px] space-y-1">
              <Label className="text-xs">Title</Label>
              <Input
                value={effectiveLesson.title}
                onChange={(e) => setDraft({ ...effectiveLesson, title: e.target.value })}
                disabled={isBusy}
                className="h-8 text-sm"
              />
            </div>
            {/* Type */}
            <div className="space-y-1 w-28">
              <Label className="text-xs">Type</Label>
              <select
                value={effectiveLesson.type}
                onChange={(e) => setDraft({ ...effectiveLesson, type: e.target.value as LessonDetails["type"] })}
                className="h-8 w-full px-2 rounded-md border border-border bg-background text-xs text-foreground"
                disabled={isBusy}
              >
                <option value="VIDEO">Video</option>
                <option value="PDF">PDF</option>
                <option value="AUDIO">Audio</option>
                <option value="LIVE">Live</option>
              </select>
            </div>
            {/* Duration */}
            <div className="space-y-1 w-28">
              <Label className="text-xs">Duration (sec)</Label>
              <Input
                type="number"
                value={effectiveLesson.duration ?? ""}
                onChange={(e) => setDraft({ ...effectiveLesson, duration: parseDurationSeconds(e.target.value) })}
                placeholder="e.g. 600"
                className="h-8 text-xs"
                disabled={isBusy}
              />
            </div>
            {/* Preview toggle */}
            <div className="flex items-center gap-2 pt-6">
              <Switch
                id={`preview-${lesson.id}`}
                checked={Boolean(effectiveLesson.isPreview)}
                onCheckedChange={(checked) => setDraft({ ...effectiveLesson, isPreview: checked })}
                disabled={isBusy}
              />
              <Label htmlFor={`preview-${lesson.id}`} className="text-xs cursor-pointer select-none">
                Free Preview (No Login Required)
              </Label>
            </div>
          </div>

          {/* Description and Objectives */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Description / Summary</Label>
              <textarea
                value={effectiveLesson.description ?? ""}
                onChange={(e) => setDraft({ ...effectiveLesson, description: e.target.value })}
                placeholder="Lesson overview..."
                disabled={isBusy}
                rows={3}
                className="w-full rounded-md border border-input bg-transparent px-3 py-1.5 text-xs shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Learning Objectives (one per line)</Label>
              <textarea
                value={effectiveLesson.objectives ?? ""}
                onChange={(e) => setDraft({ ...effectiveLesson, objectives: e.target.value })}
                placeholder="e.g. Understand core concepts&#10;Practice grammar structures..."
                disabled={isBusy}
                rows={3}
                className="w-full rounded-md border border-input bg-transparent px-3 py-1.5 text-xs shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
              />
            </div>
          </div>

          {/* File upload / URL */}
          {(effectiveLesson.type === "VIDEO" || effectiveLesson.type === "PDF" || effectiveLesson.type === "AUDIO") && (
            <div className="flex flex-wrap items-center gap-3 p-3 rounded-lg border border-border/60 bg-background">
              <ThemedFilePicker
                accept={
                  effectiveLesson.type === "VIDEO" ? "video/*"
                    : effectiveLesson.type === "PDF" ? "application/pdf"
                      : "audio/*"
                }
                disabled={isBusy}
                onPick={(file) => uploadFileMutation.mutate(file)}
                label={uploadFileMutation.isPending ? `Uploading… ${uploadProgress}%` : "Upload file"}
              />
              {effectiveLesson.contentUrl && (
                <a
                  href={effectiveLesson.contentUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="text-xs font-medium text-primary underline truncate max-w-[200px]"
                >
                  View current file ↗
                </a>
              )}
              {!effectiveLesson.contentUrl && !uploadFileMutation.isPending && (
                <span className="text-xs text-muted-foreground">No file uploaded yet</span>
              )}
            </div>
          )}

          {effectiveLesson.type === "LIVE" && (
            <div className="space-y-3 p-3 rounded-lg border border-border/60 bg-background">
              <div className="space-y-1.5">
                <Label className="text-xs">Meeting Link</Label>
                <Input
                  value={effectiveLesson.contentUrl ?? ""}
                  onChange={(e) => setDraft({ ...effectiveLesson, contentUrl: e.target.value })}
                  placeholder="https://meet.google.com/… or Zoom link"
                  disabled={isBusy}
                  className="text-sm"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">Start time</Label>
                  <Input
                    type="datetime-local"
                    value={toDatetimeLocalValue(effectiveLesson.liveStartAt)}
                    onChange={(e) => setDraft({ ...effectiveLesson, liveStartAt: e.target.value ? new Date(e.target.value).toISOString() : null })}
                    disabled={isBusy}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">End time</Label>
                  <Input
                    type="datetime-local"
                    value={toDatetimeLocalValue(effectiveLesson.liveEndAt)}
                    onChange={(e) => setDraft({ ...effectiveLesson, liveEndAt: e.target.value ? new Date(e.target.value).toISOString() : null })}
                    disabled={isBusy}
                  />
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setDraft(null);
                setExpanded(false);
              }}
            >
              Cancel
            </Button>
            <Button size="sm" onClick={handleUpdate} disabled={isBusy} className="gap-1.5">
              <Check size={13} />
              {updateLessonMutation.isPending ? "Saving…" : "Save Lesson"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// --- Exam Row ---

function ExamRow({
  exam,
  courseId,
  onNavigateToEditor,
}: {
  exam: Exam;
  courseId: string;
  onNavigateToEditor: () => void;
}) {
  const displayTitle = exam.title?.trim() || (exam.type === "CHAPTER" ? "Chapter Exam" : "Final Exam");
  const isPublished = exam.isPublished ?? false;

  return (
    <div
      className="group flex items-center gap-2.5 px-3 py-2.5 rounded-lg border border-border/60 bg-card hover:bg-muted/30 transition-colors cursor-pointer"
      onClick={onNavigateToEditor}
    >
      <ClipboardList size={14} className="text-blue-500 shrink-0" />
      <div className="flex-1 min-w-0">
        <span className="text-sm font-medium text-foreground">
          {displayTitle}
        </span>
        <span className="ml-2 text-[11px] text-muted-foreground">
          {exam.totalMarks} marks · {exam.duration}min · max {exam.maxAttempts} attempts
        </span>
      </div>
      <span className={cn(
        "text-[10px] font-semibold px-2 py-0.5 rounded-full border shrink-0 mr-1",
        isPublished
          ? "bg-green-500/10 text-green-700 border-green-500/20"
          : "bg-yellow-500/10 text-yellow-700 border-yellow-500/20"
      )}>
        {isPublished ? "Published" : "Draft"}
      </span>
      <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-blue-50 text-blue-600 shrink-0">
        EXAM
      </span>
      <ChevronRight size={13} className="text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
    </div>
  );
}

// --- Chapter Card ---

function ChapterCard({
  courseId,
  chapter,
  index,
  exams,
  onDeleteChapter,
  onDeleteLesson,
  onAddExam,
  disabled,
  onMoveUp,
  onMoveDown,
  isLast,
}: {
  courseId: string;
  chapter: ChapterDetails;
  index: number;
  exams: Exam[];
  onDeleteChapter: (chapter: ChapterDetails) => void;
  onDeleteLesson: (chapter: ChapterDetails, lesson: LessonDetails) => void;
  onAddExam: (chapterId: string) => void;
  disabled?: boolean;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  isLast?: boolean;
}) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState(chapter.title);
  const [showAddMenu, setShowAddMenu] = useState(false);
  const addMenuRef = useRef<HTMLDivElement>(null);

  // Lesson drag state
  const [localLessons, setLocalLessons] = useState<LessonDetails[]>([]);
  const [draggedLessonIdx, setDraggedLessonIdx] = useState<number | null>(null);

  useEffect(() => {
    if (chapter.lessons) {
      const sorted = [...chapter.lessons].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      setLocalLessons(sorted);
    }
  }, [chapter.lessons]);

  const handleMoveLesson = async (idx: number, direction: "up" | "down") => {
    const targetIdx = direction === "up" ? idx - 1 : idx + 1;
    if (targetIdx < 0 || targetIdx >= localLessons.length) return;

    const reordered = [...localLessons];
    const temp = reordered[idx];
    reordered[idx] = reordered[targetIdx];
    reordered[targetIdx] = temp;

    // Optimistically update UI
    setLocalLessons(reordered);

    try {
      const ids = reordered.map((l) => l.id);
      await reorderLessons(courseId, String(chapter.id), ids);
      toast.success("Lesson order saved");
    } catch (error: any) {
      toast.error(error?.message || "Failed to save lesson order");
    } finally {
      queryClient.invalidateQueries({ queryKey: ["courseChapters", courseId] });
    }
  };

  // Close menu on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (addMenuRef.current && !addMenuRef.current.contains(e.target as Node)) {
        setShowAddMenu(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const updateChapterMutation = useMutation({
    mutationFn: (dto: any) => updateChapter(courseId, chapter.id, dto),
    onSuccess: () => {
      toast.success("Chapter updated");
      setEditingTitle(false);
      queryClient.invalidateQueries({ queryKey: ["courseChapters", courseId] });
    },
    onError: (err: any) => {
      toast.error(err?.message || "Failed to update chapter");
    },
  });

  const createLessonMutation = useMutation({
    mutationFn: (order: number) =>
      createLesson(courseId, chapter.id, {
        title: `Lesson ${order + 1}`,
        type: "VIDEO",
        order,
      }),
    onSuccess: () => {
      toast.success("Lesson added");
      setShowAddMenu(false);
      queryClient.invalidateQueries({ queryKey: ["courseChapters", courseId] });
    },
    onError: (err: any) => {
      toast.error(err?.message || "Failed to create lesson");
    },
  });

  const handleSaveTitle = () => {
    updateChapterMutation.mutate({
      title: titleDraft.trim() || chapter.title,
      order: index,
    });
  };

  const handleAddLesson = () => {
    const order = localLessons.length > 0 ? localLessons[localLessons.length - 1].order + 1 : 0;
    createLessonMutation.mutate(order);
  };

  const isBusy = updateChapterMutation.isPending || createLessonMutation.isPending || disabled;
  const chapterExams = exams.filter((e) => e.chapterId === String(chapter.id));

  return (
    <div
      className="rounded-2xl border border-border bg-card shadow-sm"
    >
      {/* Chapter header */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-border/60 bg-muted/20 rounded-t-2xl">
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="text-muted-foreground hover:text-foreground transition-colors"
        >
          <ChevronDown
            size={15}
            className={cn("transition-transform duration-200", collapsed && "-rotate-90")}
          />
        </button>

        {/* Chapter title */}
        <div className="flex-1 min-w-0">
          {editingTitle ? (
            <div className="flex items-center gap-2">
              <Input
                value={titleDraft}
                onChange={(e) => setTitleDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleSaveTitle();
                  if (e.key === "Escape") { setEditingTitle(false); setTitleDraft(chapter.title); }
                }}
                className="h-7 text-sm font-semibold"
                autoFocus
                disabled={isBusy}
              />
              <Button size="sm" className="h-7 px-2" onClick={handleSaveTitle} disabled={isBusy}>
                <Check size={13} />
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="h-7 px-2"
                onClick={() => { setEditingTitle(false); setTitleDraft(chapter.title); }}
              >
                <X size={13} />
              </Button>
            </div>
          ) : (
            <button
              className="flex items-center gap-2 group/title"
              onClick={() => { setEditingTitle(true); setTitleDraft(chapter.title); }}
            >
              <span className="text-sm font-semibold text-foreground">{chapter.title}</span>
              <PenLine size={12} className="text-muted-foreground opacity-0 group-hover/title:opacity-100 transition-opacity" />
            </button>
          )}
        </div>

        {/* Chapter meta */}
        <div className="flex items-center gap-2 text-[11px] text-muted-foreground shrink-0">
          <span>{localLessons.length} lesson{localLessons.length !== 1 ? "s" : ""}</span>
          {chapterExams.length > 0 && (
            <span className="text-blue-500">· {chapterExams.length} exam{chapterExams.length !== 1 ? "s" : ""}</span>
          )}
        </div>

        {/* Up/Down reorder buttons */}
        <div className="flex items-center gap-0.5 shrink-0">
          <Button
            variant="ghost"
            size="icon"
            className="size-6 text-muted-foreground hover:text-foreground rounded-md"
            disabled={index === 0 || isBusy}
            onClick={(e) => {
              e.stopPropagation();
              onMoveUp?.();
            }}
            title="Move up"
          >
            <ArrowUp size={12} />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="size-6 text-muted-foreground hover:text-foreground rounded-md"
            disabled={isLast || isBusy}
            onClick={(e) => {
              e.stopPropagation();
              onMoveDown?.();
            }}
            title="Move down"
          >
            <ArrowDown size={12} />
          </Button>
        </div>

        <button
          onClick={() => onDeleteChapter(chapter)}
          className="text-muted-foreground hover:text-destructive transition-colors p-1 rounded"
          disabled={isBusy}
        >
          <Trash2 size={14} />
        </button>
      </div>

      {/* Chapter content */}
      {!collapsed && (
        <div className="p-4 space-y-2">
          {/* Lessons */}
          {localLessons.map((l, lessonIndex) => (
            <LessonRow
              key={l.id}
              courseId={courseId}
              chapterId={chapter.id}
              lesson={l}
              index={lessonIndex}
              onDelete={(lesson) => onDeleteLesson(chapter, lesson)}
              disabled={disabled}
              isDragging={draggedLessonIdx === lessonIndex}
              onDragStart={(e) => {
                setDraggedLessonIdx(lessonIndex);
                e.stopPropagation();
                e.dataTransfer.effectAllowed = "move";
                e.dataTransfer.setData("text/plain", String(lessonIndex));
              }}
              onDragOver={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (draggedLessonIdx === null || draggedLessonIdx === lessonIndex) return;

                const reordered = [...localLessons];
                const [removed] = reordered.splice(draggedLessonIdx, 1);
                reordered.splice(lessonIndex, 0, removed);
                setDraggedLessonIdx(lessonIndex);
                setLocalLessons(reordered);
              }}
              onDragEnd={async () => {
                setDraggedLessonIdx(null);
                try {
                  const ids = localLessons.map((l) => l.id);
                  const origIds = chapter.lessons?.map((les) => les.id) || [];
                  const hasChanged = ids.some((id, index) => origIds[index] !== id);
                  if (hasChanged) {
                    await reorderLessons(courseId, String(chapter.id), ids);
                    toast.success("Lesson order saved");
                  }
                } catch (error: any) {
                  toast.error(error?.message || "Failed to save lesson order");
                } finally {
                  queryClient.invalidateQueries({ queryKey: ["courseChapters", courseId] });
                }
              }}
              onMoveUp={() => handleMoveLesson(lessonIndex, "up")}
              onMoveDown={() => handleMoveLesson(lessonIndex, "down")}
              isLast={lessonIndex === localLessons.length - 1}
            />
          ))}

          {/* Exams */}
          {chapterExams.map((exam) => (
            <ExamRow
              key={exam.id}
              exam={exam}
              courseId={courseId}
              onNavigateToEditor={() => {
                // Navigate to the create-exam page with course + chapter context
                window.location.href = `/admin/create-exam?courseId=${courseId}&chapterId=${chapter.id}&examId=${exam.id}`;
              }}
            />
          ))}

          {localLessons.length === 0 && chapterExams.length === 0 && (
            <div className="dash-empty-state py-8 my-2">
              <p className="text-sm text-muted-foreground">
                No content yet. Use <span className="font-medium text-foreground">+ Add Content</span> to get started.
              </p>
            </div>
          )}

          {/* Add Content button */}
          <div className="relative pt-1" ref={addMenuRef}>
            <Button
              variant="ghost"
              size="sm"
              className="gap-1.5 text-primary hover:bg-primary/10 text-xs font-medium"
              disabled={isBusy}
              onClick={() => setShowAddMenu(!showAddMenu)}
            >
              <Plus size={14} />
              Add Content
              <ChevronDown size={12} className={cn("transition-transform duration-150", showAddMenu && "rotate-180")} />
            </Button>

            {showAddMenu && (
              <div className="absolute left-0 top-full mt-1 z-20 w-52 rounded-xl border border-border bg-card shadow-lg overflow-hidden">
                <button
                  className="w-full flex items-center gap-3 px-4 py-3 text-sm text-foreground hover:bg-muted/50 transition-colors"
                  onClick={handleAddLesson}
                  disabled={createLessonMutation.isPending}
                >
                  <div className="w-7 h-7 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                    <Video size={13} className="text-primary" />
                  </div>
                  <div className="text-left">
                    <p className="font-medium text-sm">Add Lesson</p>
                    <p className="text-[11px] text-muted-foreground">Video, PDF, Audio, or Live</p>
                  </div>
                </button>
                {chapterExams.length === 0 && (
                  <>
                    <div className="border-t border-border/60" />
                    <button
                      className="w-full flex items-center gap-3 px-4 py-3 text-sm text-foreground hover:bg-muted/50 transition-colors"
                      onClick={() => {
                        setShowAddMenu(false);
                        onAddExam(chapter.id);
                      }}
                    >
                      <div className="w-7 h-7 rounded-lg bg-blue-50 flex items-center justify-center shrink-0">
                        <ClipboardList size={13} className="text-blue-600" />
                      </div>
                      <div className="text-left">
                        <p className="font-medium text-sm">Add Exam</p>
                        <p className="text-[11px] text-muted-foreground">Chapter quiz or assessment</p>
                      </div>
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// --- Main Page ---

export default function AdminCourseStructurePage() {
  const { courseId } = useParams<{ courseId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const resolvedCourseId = courseId ?? "";
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const [deleteConfirmTarget, setDeleteConfirmTarget] = useState<
    | { kind: "chapter"; chapter: ChapterDetails }
    | { kind: "lesson"; chapter: ChapterDetails; lesson: LessonDetails }
    | null
  >(null);

  const courseQuery = useQuery({
    queryKey: ["adminCourse", resolvedCourseId],
    queryFn: () => getCourse(resolvedCourseId),
    enabled: Boolean(resolvedCourseId),
  });

  const chaptersQuery = useQuery({
    queryKey: ["courseChapters", resolvedCourseId],
    queryFn: () => getCourseChapters(resolvedCourseId),
    enabled: Boolean(resolvedCourseId),
  });

  const examsQuery = useQuery({
    queryKey: ["courseExams", resolvedCourseId],
    queryFn: () => getCourseExams(resolvedCourseId),
    enabled: Boolean(resolvedCourseId),
  });

  const [localChapters, setLocalChapters] = useState<ChapterDetails[]>([]);
  const [draggedChapterIdx, setDraggedChapterIdx] = useState<number | null>(null);

  useEffect(() => {
    if (chaptersQuery.data) {
      const sorted = [...chaptersQuery.data].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      setLocalChapters(sorted);
    }
  }, [chaptersQuery.data]);

  const handleMoveChapter = async (idx: number, direction: "up" | "down") => {
    const targetIdx = direction === "up" ? idx - 1 : idx + 1;
    if (targetIdx < 0 || targetIdx >= localChapters.length) return;

    const reordered = [...localChapters];
    const temp = reordered[idx];
    reordered[idx] = reordered[targetIdx];
    reordered[targetIdx] = temp;

    // Optimistically update UI
    setLocalChapters(reordered);

    try {
      const ids = reordered.map((ch) => ch.id);
      await reorderChapters(resolvedCourseId, ids);
      toast.success("Section order saved");
    } catch (error: any) {
      toast.error(error?.message || "Failed to save section order");
    } finally {
      queryClient.invalidateQueries({ queryKey: ["courseChapters", resolvedCourseId] });
    }
  };

  const updateCourseMutation = useMutation({
    mutationFn: (dto: Parameters<typeof updateCourse>[1]) => updateCourse(resolvedCourseId, dto),
    onSuccess: () => {
      toast.success("Course updated");
      queryClient.invalidateQueries({ queryKey: ["adminCourse", resolvedCourseId] });
      queryClient.invalidateQueries({ queryKey: ["lmsCourses"] });
    },
    onError: (err: any) => toast.error(err?.message || "Failed to update course"),
  });

  const createChapterMutation = useMutation({
    mutationFn: (order: number) =>
      createChapter(resolvedCourseId, {
        title: `Chapter ${order + 1}`,
        order,
      }),
    onSuccess: () => {
      toast.success("Chapter added");
      queryClient.invalidateQueries({ queryKey: ["courseChapters", resolvedCourseId] });
    },
    onError: (err: any) => {
      toast.error(err?.message || "Failed to create chapter");
    },
  });

  const deleteChapterMutation = useMutation({
    mutationFn: (chapterId: string) => deleteChapter(resolvedCourseId, chapterId),
    onSuccess: () => {
      toast.success("Chapter deleted");
      setDeleteConfirmTarget(null);
      queryClient.invalidateQueries({ queryKey: ["courseChapters", resolvedCourseId] });
    },
    onError: (err: any) => {
      toast.error(err?.message || "Failed to delete chapter");
    },
  });

  const deleteLessonMutation = useMutation({
    mutationFn: ({ chapterId, lessonId }: { chapterId: string; lessonId: string }) =>
      deleteLesson(resolvedCourseId, chapterId, lessonId),
    onSuccess: () => {
      toast.success("Lesson deleted");
      setDeleteConfirmTarget(null);
      queryClient.invalidateQueries({ queryKey: ["courseChapters", resolvedCourseId] });
    },
    onError: (err: any) => {
      toast.error(err?.message || "Failed to delete lesson");
    },
  });

  const handleCreateChapter = () => {
    if (!resolvedCourseId) return;
    const order = localChapters.length;
    createChapterMutation.mutate(order);
  };

  const confirmDelete = () => {
    if (!resolvedCourseId || !deleteConfirmTarget) return;
    const target = deleteConfirmTarget;
    if (target.kind === "chapter") {
      deleteChapterMutation.mutate(target.chapter.id);
    } else {
      deleteLessonMutation.mutate({
        chapterId: target.chapter.id,
        lessonId: target.lesson.id,
      });
    }
  };

  const handleAddExam = (chapterId: string) => {
    navigate(`/admin/create-exam?courseId=${resolvedCourseId}&chapterId=${chapterId}`);
  };

  const isGlobalLoading =
    createChapterMutation.isPending ||
    deleteChapterMutation.isPending ||
    deleteLessonMutation.isPending;

  const course = courseQuery.data;
  const courseTitle = course?.title ?? "Course Builder";
  const exams = examsQuery.data?.items ?? [];
  const isLoading = courseQuery.isLoading || chaptersQuery.isLoading;

  const totalLessons = localChapters.reduce((sum, ch) => sum + (ch.lessons?.length ?? 0), 0);

  return (
    <DashboardLayout>
      <div className="dash-page max-w-4xl mx-auto">
        {/* ─── Top bar ────────────────────────────────────────────────── */}
        <div className="dash-page-header mb-6">
          <div className="flex flex-col sm:flex-row sm:items-start gap-4">
            <div className="flex-1 min-w-0">
              <Button
                variant="ghost"
                size="sm"
                className="gap-2 text-muted-foreground mb-2 -ml-2"
                onClick={() => navigate("/admin/courses")}
              >
                <ArrowLeft size={15} />
                Back to Courses
              </Button>
              {isLoading ? (
                <>
                  <Skeleton className="h-8 w-64 mb-2" />
                  <Skeleton className="h-4 w-48" />
                </>
              ) : (
                <>
                  <h1 className="dash-page-title truncate">{courseTitle}</h1>
                  <div className="flex items-center gap-3 mt-2 flex-wrap">
                    <span className={cn(
                      "inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full",
                      course?.isPublished
                        ? "bg-emerald-50 text-emerald-700"
                        : "bg-amber-50 text-amber-600"
                    )}>
                      {course?.isPublished ? <Globe size={10} /> : <EyeOff size={10} />}
                      {course?.isPublished ? "Published" : "Draft"}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {localChapters.length} chapter{localChapters.length !== 1 ? "s" : ""} · {totalLessons} lesson{totalLessons !== 1 ? "s" : ""}
                    </span>
                    {exams.length > 0 && (
                      <span className="text-xs text-blue-500">
                        · {exams.length} exam{exams.length !== 1 ? "s" : ""}
                      </span>
                    )}
                  </div>
                </>
              )}
            </div>

            <div className="flex items-center gap-2 shrink-0">
              {/* Publish toggle */}
              {course && (
                <div className="flex items-center gap-2 px-3 py-1.5 bg-card border border-border rounded-xl">
                  <span className="text-xs font-medium text-muted-foreground">Published</span>
                  <Switch
                    checked={Boolean(course.isPublished)}
                    onCheckedChange={(checked) => {
                      updateCourseMutation.mutate({ isPublished: checked });
                    }}
                    disabled={updateCourseMutation.isPending}
                  />
                </div>
              )}

              <Button
                onClick={handleCreateChapter}
                disabled={!resolvedCourseId || isGlobalLoading}
                className="gap-2"
              >
                <Plus size={15} />
                {createChapterMutation.isPending ? "Adding…" : "Add Section"}
              </Button>
            </div>
          </div>
        </div>

        {/* ─── Teacher access ──────────────────────────────────────────── */}
        <TeacherAssignmentSection courseId={resolvedCourseId} isAdmin={isAdmin} />

        {/* ─── Course structure ────────────────────────────────────────── */}
        {isLoading ? (
          <div className="space-y-3">
            <Skeleton className="h-14 w-full rounded-2xl" />
            <Skeleton className="h-36 w-full rounded-2xl" />
            <Skeleton className="h-36 w-full rounded-2xl" />
          </div>
        ) : localChapters.length === 0 ? (
          <div className="dash-empty-state">
            <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
              <Layers className="text-primary" size={28} />
            </div>
            <h3 className="font-semibold text-foreground text-lg mb-2">Start building your course</h3>
            <p className="text-sm text-muted-foreground mb-6 max-w-sm mx-auto">
              Add your first section (chapter) to organize your lessons and exams.
            </p>
            <Button onClick={handleCreateChapter} disabled={isGlobalLoading} className="gap-2">
              <Plus size={15} />
              Add First Section
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {localChapters.map((ch, idx) => (
              <ChapterCard
                key={ch.id}
                courseId={resolvedCourseId}
                chapter={ch}
                index={idx}
                exams={exams}
                onDeleteChapter={(c) => setDeleteConfirmTarget({ kind: "chapter", chapter: c })}
                onDeleteLesson={(c, l) => setDeleteConfirmTarget({ kind: "lesson", chapter: c, lesson: l })}
                onAddExam={handleAddExam}
                disabled={isGlobalLoading}

                onMoveUp={() => handleMoveChapter(idx, "up")}
                onMoveDown={() => handleMoveChapter(idx, "down")}
                isLast={idx === localChapters.length - 1}
              />
            ))}

            {/* Add section button */}
            <button
              onClick={handleCreateChapter}
              disabled={isGlobalLoading}
              className="w-full flex items-center justify-center gap-2 py-3 rounded-2xl border-2 border-dashed border-border text-sm text-muted-foreground hover:text-foreground hover:border-border/80 hover:bg-muted/20 transition-colors"
            >
              <Plus size={15} />
              Add Section
            </button>
          </div>
        )}
      </div>

      {/* ─── Delete Confirm Dialog ───────────────────────────────────────── */}
      <Dialog
        open={Boolean(deleteConfirmTarget)}
        onOpenChange={(open) => !open && !isGlobalLoading && setDeleteConfirmTarget(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm deletion</DialogTitle>
            <DialogDescription>
              {deleteConfirmTarget?.kind === "chapter"
                ? `Delete chapter "${deleteConfirmTarget.chapter.title}"? All lessons inside will also be deleted.`
                : deleteConfirmTarget?.kind === "lesson"
                  ? `Delete lesson "${deleteConfirmTarget.lesson.title}"?`
                  : ""}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setDeleteConfirmTarget(null)}
              disabled={isGlobalLoading}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDelete}
              disabled={isGlobalLoading}
            >
              {isGlobalLoading ? "Deleting…" : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
